import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { unstableSetRender } from "antd";
import "./index.css";
import App from "./App";

// A deploy replaces every content-hashed chunk, so a tab left open across one gets a
// 404 when it finally runs a lazy import(). Vite raises this event for exactly that
// case; reloading fetches the new index.html and its new hashes. Throttled so a chunk
// that is genuinely missing (bad deploy, blocked request) cannot boot-loop the app.
// ponytail: recovery, not prevention — the reload still costs the user their click.
// Prevention is keeping old /assets/* alive across deploys (sync without --delete, a
// lifecycle rule to expire them, `base` pointed at that origin), which means moving
// assets off Amplify's managed deploy. Worth it only if this starts hitting more users.
window.addEventListener("vite:preloadError", () => {
  const key = "vite:preloadError:reloadedAt";
  const last = Number(sessionStorage.getItem(key)) || 0;
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(key, String(Date.now()));
  window.location.reload();
});

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isBetween from "dayjs/plugin/isBetween";
import minMax from "dayjs/plugin/minMax";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import isoWeek from "dayjs/plugin/isoWeek";

// Ant Design 5 static APIs (notification.error, message.success, etc.) rely on
// rc-util's render shim which looks for createRoot in 'react-dom'. React 19 moved
// it to 'react-dom/client', so the shim falls back to the removed ReactDOM.render
// and silently does nothing. This call provides the correct render path.
unstableSetRender((node, container) => {
  (container as any)._reactRoot ??= createRoot(container);
  const root = (container as any)._reactRoot;
  root.render(node);
  return async () => {
    await new Promise(resolve => setTimeout(resolve, 0));
    root.unmount();
  };
});
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(minMax);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(isoWeek);
import { Provider } from "react-redux";
import { applyMiddleware, createStore } from "redux";
import thunk from "redux-thunk";
import rootReducer from "./store/reducers";
import { App as AntApp } from "antd";
import { Auth } from "./Components/Auth/Auth";
import { navigateRef } from "@utils/navigate_ref";
import { BrowserRouter as Router, useNavigate } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import {
  successfulLoginRedirectUrl,
  authzClientId,
  authzDomain,
  authzAudience,
} from "./auth/auth0";
import { initSentry } from "./sentry";
import posthog from 'posthog-js';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { initDatadogErrorGrouping, dropIgnoredClientErrors } from './datadog';
import { initNetworkNotice } from './networkNotice';

if (import.meta.env.MODE === "production") {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DD_APPLICATION_ID || '',
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN || '',
    site: 'datadoghq.com',
    service: 'whistle-web-platform',
    env: import.meta.env.MODE,
    version: import.meta.env.VITE_SENTRY_RELEASE || '0.1.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    trackViewsManually: false,
    plugins: [reactPlugin()],
    beforeSend: dropIgnoredClientErrors,
  });
  initDatadogErrorGrouping();
}

// Not gated on production: a user on a dead connection should see this
// in every environment.
initNetworkNotice();

const store = createStore(rootReducer, applyMiddleware(thunk));
if (import.meta.env.MODE !== "development") {
  initSentry();
}

if (import.meta.env.MODE === "production") {
  posthog.init("phc_DzATNluXUi5XpCnJ6QmzE9IT1pslYH2bbKfEkGhpQhi", {
    api_host: "https://us.i.posthog.com",
  });
}



// Custom Auth0ProviderWrapper to use React Router's navigation
const Auth0ProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const onRedirectCallback = (appState: any) => {
    const targetUrl = "/team-selection";
    console.log("appstate", appState)
    const query = appState?.returnTo ? `?returnTo=${encodeURIComponent(appState.returnTo)}` : "";
    navigate(targetUrl + query);
  };

  return (
    <Auth0Provider
      domain={authzDomain}
      clientId={authzClientId}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      authorizationParams={{
        audience: authzAudience,
        redirect_uri: successfulLoginRedirectUrl,
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

// Root component with Provider, Router, and Auth0 integration
const Root = () => (
  <Provider store={store}>
    <Router>
      <Auth0ProviderWrapper>
        <AntApp>
          <Auth>
            <App />
          </Auth>
        </AntApp>
      </Auth0ProviderWrapper>
    </Router>
  </Provider>
);

const container = document.getElementById("root")!;
createRoot(container).render(<Root />);